import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, Heart, Car, Umbrella, Zap, ArrowLeft, Download,
  Users, Calendar, DollarSign, Sparkles, Loader2, Globe, X,
  ChevronRight, FileText, Bed, Clock, AlertTriangle, CheckCircle,
  UserCircle, Star
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const CATMETA = {
  Health: { color: "#22c55e", bg: "bg-green-500/10", text: "text-green-600", border: "border-green-500/30", icon: Heart, label: "Health Insurance" },
  Life: { color: "#f59e0b", bg: "bg-[#D4A017]/10", text: "text-[#A87608]", border: "border-[#D4A017]/30", icon: Umbrella, label: "Life Insurance" },
  Vehicle: { color: "#3b82f6", bg: "bg-[#EDF1F8]/30", text: "text-[#2E3B57]", border: "border-blue-500/30", icon: Car, label: "Vehicle Insurance" },
  PA: { color: "#f43f5e", bg: "bg-rose-500/10", text: "text-rose-600", border: "border-rose-500/30", icon: Zap, label: "Personal Accident" }
};

function RichText({ text }) {
  if (!text) return null;
  return (
    <div className="space-y-1">
      {text.split('\n').map((line, i) => {
        let processed = line.replace(/^#{1,3}\s*/, '');
        const parts = processed.split(/\*\*(.*?)\*\*/g);
        if (parts.length === 1) return <p key={i} className="text-[#2E3B57] text-sm leading-relaxed">{processed}</p>;
        return (
          <p key={i} className="text-[#2E3B57] text-sm leading-relaxed">
            {parts.map((part, j) => j % 2 === 1 ? <strong key={j} className="font-bold text-[#1B2A4A]">{part}</strong> : part)}
          </p>
        );
      })}
    </div>
  );
}

function FeatureRow({ icon: Icon, label, value, color, onExplain }) {
  if (!value || value === "null" || value === "N/A" || value === "None") return null;
  return (
    <div className="bg-[#EDF1F8]/40 rounded-xl p-3.5 flex items-center gap-3 border border-[#C9D4E6]/50">
      <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15`, borderColor: `${color}30`, borderWidth: 1 }}>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-[#3A4557] font-medium uppercase tracking-wide">{label}</p>
        <p className="text-sm font-semibold text-[#1B2A4A] mt-0.5">{value}</p>
      </div>
      {onExplain && (
        <button onClick={onExplain} className="h-8 w-8 rounded-lg bg-[#D4A017]/10 border border-[#D4A017]/20 flex items-center justify-center hover:bg-[#D4A017]/15 transition-colors"
          data-testid={`explain-${label.toLowerCase().replace(/\s+/g, '-')}`}>
          <Sparkles className="h-3.5 w-3.5 text-[#A87608]" />
        </button>
      )}
    </div>
  );
}

function ExplainerSheet({ title, subtitle, onClose, children }) {
  return (
    <>
      <motion.div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
      <motion.div className="fixed z-50 bg-white border border-[#C9D4E6] rounded-2xl shadow-2xl w-[95%] max-w-lg left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[80vh] flex flex-col"
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}>
        <div className="px-5 py-4 flex items-center justify-between border-b border-[#C9D4E6] flex-shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#A87608]" />
            <div>
              <p className="font-bold text-[#1B2A4A] text-sm">{title}</p>
              {subtitle && <p className="text-[10px] text-[#3A4557]">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="h-7 w-7 rounded-lg bg-[#EDF1F8] flex items-center justify-center hover:bg-[#E2E9F4]">
            <X className="h-4 w-4 text-[#3A4557]" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </motion.div>
    </>
  );
}

export default function ClientPolicyDetail() {
  const { policyId } = useParams();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [explainFeature, setExplainFeature] = useState(null);
  const [featureExplanation, setFeatureExplanation] = useState("");
  const [featureLang, setFeatureLang] = useState("english");
  const [featureLoading, setFeatureLoading] = useState(false);
  const [fullExplainOpen, setFullExplainOpen] = useState(false);
  const [fullExplanation, setFullExplanation] = useState("");
  const [fullLang, setFullLang] = useState("english");
  const [fullLoading, setFullLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("client_token");
    if (!token) { navigate("/client/login"); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/api/client/policy/${policyId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.status === 401) { navigate("/client/login"); return; }
        const data = await res.json();
        if (data.success) setPolicy(data.policy);
      } catch {}
      finally { setLoading(false); }
    })();
  }, [policyId]);

  const fetchFeatureExplain = async (name, value, lang) => {
    setFeatureLoading(true); setFeatureExplanation("");
    try {
      const token = localStorage.getItem("client_token");
      const res = await fetch(`${API}/api/client/explain-feature`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ feature_name: name, feature_value: value, policy_context: `${policy?.insurance_company} ${policy?.plan_name}`, language: lang })
      });
      const data = await res.json();
      setFeatureExplanation(res.ok && data.success ? data.explanation : "Unable to explain.");
    } catch { setFeatureExplanation("Connection error."); }
    finally { setFeatureLoading(false); }
  };

  const fetchFullExplain = async (lang) => {
    setFullLoading(true); setFullExplanation("");
    try {
      const token = localStorage.getItem("client_token");
      const res = await fetch(`${API}/api/client/explain-policy`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ policy_id: policyId, language: lang })
      });
      const data = await res.json();
      setFullExplanation(res.ok && data.success ? data.explanation : "Unable to explain.");
    } catch { setFullExplanation("Connection error."); }
    finally { setFullLoading(false); }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("client_token");
      const res = await fetch(`${API}/api/client/policy/${policyId}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const blob = await res.blob(); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `policy_${policy?.policy_number || policyId}.pdf`; a.click(); }
    } catch {}
    finally { setDownloading(false); }
  };

  if (loading) return <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] flex items-center justify-center"><Loader2 className="h-10 w-10 text-[#A87608] animate-spin" /></div>;
  if (!policy) return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] flex items-center justify-center p-6 text-center">
      <div><AlertTriangle className="h-12 w-12 text-[#3A4557] mx-auto mb-3" /><p className="text-[#3A4557]">Policy not found</p>
        <Button onClick={() => navigate("/client/dashboard")} className="mt-4 bg-[#D4A017] text-[#0D1A3A] font-bold">Back</Button></div>
    </div>
  );

  const cat = CATMETA[policy.insurance_category] || CATMETA.Health;
  const Icon = cat.icon;
  const isExpired = policy.policy_end_date && new Date(policy.policy_end_date) < new Date();
  const daysLeft = policy.policy_end_date ? Math.ceil((new Date(policy.policy_end_date) - new Date()) / 86400000) : null;
  const isExpiring = daysLeft !== null && daysLeft > 0 && daysLeft <= 30;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-[#C9D4E6]">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 md:px-8 py-3">
          <button onClick={() => navigate("/client/dashboard")} className="h-9 w-9 rounded-lg bg-[#EDF1F8] hover:bg-[#E2E9F4] flex items-center justify-center" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 text-[#3A4557]" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-[#1B2A4A] truncate">{policy.plan_name}</p>
            <p className="text-[10px] text-[#3A4557]">{policy.insurance_company}</p>
          </div>
          {policy.has_document && (
            <button onClick={handleDownload} disabled={downloading} className="h-9 w-9 rounded-lg bg-[#D4A017]/10 border border-[#D4A017]/30 flex items-center justify-center hover:bg-[#D4A017]/15" data-testid="download-policy-btn">
              {downloading ? <Loader2 className="h-4 w-4 text-[#A87608] animate-spin" /> : <Download className="h-4 w-4 text-[#A87608]" />}
            </button>
          )}
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 md:px-8 pb-12">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="mt-5 rounded-2xl p-5 md:p-6 border" style={{ backgroundColor: `${cat.color}08`, borderColor: `${cat.color}20` }}
          data-testid="policy-hero">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${cat.color}15` }}>
              <Icon className="h-6 w-6" style={{ color: cat.color }} />
            </div>
            <div>
              <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ backgroundColor: `${cat.color}15`, color: cat.color }}>{cat.label}</span>
              <h1 className="text-xl md:text-2xl font-black text-[#1B2A4A] mt-1.5">{policy.plan_name}</h1>
              <p className="text-xs text-[#3A4557]">{policy.insurance_company}</p>
            </div>
          </div>
          {isExpired ? (
            <div className="mt-3 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" /><span className="text-xs font-semibold text-red-600">Policy Expired - Contact advisor for renewal</span>
            </div>
          ) : isExpiring ? (
            <div className="mt-3 bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl px-3 py-2 flex items-center gap-2">
              <Clock className="h-4 w-4 text-[#A87608]" /><span className="text-xs font-semibold text-[#A87608]">{daysLeft} days left - Renewal due soon</span>
            </div>
          ) : daysLeft !== null ? (
            <div className="mt-3 bg-green-500/10 border border-green-500/30 rounded-xl px-3 py-2 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" /><span className="text-xs font-semibold text-green-600">Active - {daysLeft} days remaining</span>
            </div>
          ) : null}
        </motion.div>

        {/* Key Numbers */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-white/50 rounded-xl p-4 border border-[#C9D4E6]">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="h-3.5 w-3.5" style={{ color: cat.color }} />
              <span className="text-[10px] text-[#3A4557] uppercase tracking-wide">{policy.insurance_category === "Life" ? "Sum Assured" : "Sum Insured"}</span>
            </div>
            <p className="text-lg font-black text-[#1B2A4A]">{policy.sum_insured || policy.sum_assured || "N/A"}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-white/50 rounded-xl p-4 border border-[#C9D4E6]">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="h-3.5 w-3.5 text-[#A87608]" />
              <span className="text-[10px] text-[#3A4557] uppercase tracking-wide">Premium</span>
            </div>
            <p className="text-lg font-black text-[#1B2A4A]">₹{Number(String(policy.premium_amount || 0).replace(/,/g, '')).toLocaleString('en-IN')}</p>
            <p className="text-[10px] text-[#3A4557]">{policy.payment_frequency || "Annual"}</p>
          </motion.div>
        </div>

        {/* Policy Period */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="mt-3 bg-white/50 rounded-xl p-4 border border-[#C9D4E6]">
          <p className="text-[10px] text-[#3A4557] uppercase tracking-wide mb-3">Policy Period</p>
          <div className="flex items-center gap-3">
            <div className="flex-1 text-center">
              <Calendar className="h-4 w-4 text-green-600 mx-auto mb-1" />
              <p className="text-[10px] text-[#3A4557]">Start</p>
              <p className="text-sm font-bold text-[#1B2A4A]">{policy.policy_start_date ? new Date(policy.policy_start_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}</p>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-600" />
            <div className="flex-1 text-center">
              <Calendar className="h-4 w-4 text-red-600 mx-auto mb-1" />
              <p className="text-[10px] text-[#3A4557]">End</p>
              <p className="text-sm font-bold text-[#1B2A4A]">{policy.policy_end_date ? new Date(policy.policy_end_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}</p>
            </div>
          </div>
          {policy.policy_number && policy.policy_number !== "N/A" && (
            <div className="mt-3 pt-3 border-t border-[#C9D4E6]">
              <p className="text-[10px] text-[#3A4557]">Policy Number</p>
              <p className="text-sm font-bold text-[#2E3B57] font-mono">{policy.policy_number}</p>
            </div>
          )}
        </motion.div>

        {/* Explain Button */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <Button onClick={() => { setFullExplainOpen(true); fetchFullExplain(fullLang); }}
            className="mt-4 w-full bg-gradient-to-r from-[#D4A017] to-[#D9A82C] text-[#0D1A3A] font-bold h-12 text-sm shadow-lg shadow-[#D4A017]/15"
            data-testid="full-explain-btn">
            <Sparkles className="h-4 w-4 mr-2" /> Explain This Policy in Simple Words
          </Button>
        </motion.div>

        {/* Desktop 2-col layout for members + features */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Covered Members */}
          {(policy.family_members?.length > 0 || policy.insured_members_details?.length > 0) && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <p className="text-[10px] font-semibold text-[#3A4557] uppercase tracking-widest mb-3">Who Is Covered</p>
              <div className="space-y-2">
                {(policy.insured_members_details?.length > 0 ? policy.insured_members_details : policy.family_members?.map(n => ({ name: n }))).map((m, i) => (
                  <div key={i} className="bg-white/50 rounded-xl p-3 border border-[#C9D4E6] flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-gradient-to-r from-[#D4A017]/20 to-[#D9A82C]/10 flex items-center justify-center">
                      <UserCircle className="h-5 w-5 text-[#A87608]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[#1B2A4A]">{m.name || "Member"}</p>
                      <div className="flex gap-3 mt-0.5">
                        {m.relationship && <span className="text-[10px] text-[#3A4557]">{m.relationship}</span>}
                        {m.age && <span className="text-[10px] text-[#3A4557]">Age: {m.age}</span>}
                      </div>
                    </div>
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Features */}
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
            <p className="text-[10px] font-semibold text-[#3A4557] uppercase tracking-widest mb-3">Policy Features</p>
            <div className="space-y-2">
              <FeatureRow icon={Bed} label="Room Rent" value={policy.room_rent_limit} color="#22c55e"
                onExplain={() => { setExplainFeature({ name: "Room Rent Limit", value: policy.room_rent_limit }); fetchFeatureExplain("Room Rent Limit", policy.room_rent_limit, featureLang); }} />
              <FeatureRow icon={DollarSign} label="Co-payment" value={policy.copay_percentage} color="#f59e0b"
                onExplain={() => { setExplainFeature({ name: "Co-payment", value: policy.copay_percentage }); fetchFeatureExplain("Co-payment", policy.copay_percentage, featureLang); }} />
              <FeatureRow icon={Shield} label="Deductible" value={policy.deductible} color="#3b82f6"
                onExplain={() => { setExplainFeature({ name: "Deductible", value: policy.deductible }); fetchFeatureExplain("Deductible", policy.deductible, featureLang); }} />
              <FeatureRow icon={Clock} label="PED Waiting Period" value={policy.waiting_period_ped} color="#f43f5e"
                onExplain={() => { setExplainFeature({ name: "Pre-Existing Disease Waiting Period", value: policy.waiting_period_ped }); fetchFeatureExplain("Pre-Existing Disease Waiting Period", policy.waiting_period_ped, featureLang); }} />
              <FeatureRow icon={FileText} label="Policy Type" value={policy.policy_type} color="#8b5cf6" />
              {policy.insurance_category === "Life" && (
                <>
                  <FeatureRow icon={Umbrella} label="Plan Type" value={policy.life_plan_type} color="#f59e0b" />
                  <FeatureRow icon={Heart} label="Nominee" value={policy.nominee_name ? `${policy.nominee_name} (${policy.nominee_relationship || ''})` : null} color="#f43f5e" />
                  <FeatureRow icon={DollarSign} label="Maturity Benefit" value={policy.maturity_benefit} color="#22c55e" />
                </>
              )}
              {policy.insurance_category === "Vehicle" && (
                <>
                  <FeatureRow icon={Car} label="Vehicle" value={policy.vehicle_make ? `${policy.vehicle_make} ${policy.vehicle_model || ''}` : null} color="#3b82f6" />
                  <FeatureRow icon={FileText} label="Registration" value={policy.vehicle_registration_number} color="#8b5cf6" />
                  <FeatureRow icon={DollarSign} label="IDV" value={policy.idv} color="#f59e0b" />
                </>
              )}
            </div>
          </motion.div>
        </div>

        {/* Riders */}
        {policy.riders?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mt-6">
            <p className="text-[10px] font-semibold text-[#3A4557] uppercase tracking-widest mb-3">Riders & Add-ons</p>
            <div className="bg-white/50 rounded-xl p-4 border border-[#C9D4E6] space-y-2">
              {policy.riders.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Star className="h-3.5 w-3.5 text-[#A87608] flex-shrink-0" />
                  <span className="text-sm text-[#2E3B57]">{r}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Notes */}
        {policy.notes && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="mt-6">
            <p className="text-[10px] font-semibold text-[#3A4557] uppercase tracking-widest mb-3">Important Notes</p>
            <div className="bg-[#D4A017]/5 rounded-xl p-4 border border-[#D4A017]/20">
              <p className="text-sm text-[#8F6A06]/80 leading-relaxed">{policy.notes}</p>
            </div>
          </motion.div>
        )}

        {/* Download */}
        {policy.has_document && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <Button onClick={handleDownload} disabled={downloading} variant="outline"
              className="mt-5 w-full border-[#C9D4E6] text-[#2E3B57] hover:text-[#1B2A4A] h-11" data-testid="download-pdf-btn">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
              Download Policy Document (PDF)
            </Button>
          </motion.div>
        )}
      </div>

      {/* Feature Explainer */}
      <AnimatePresence>
        {explainFeature && (
          <ExplainerSheet title={explainFeature.name} subtitle={explainFeature.value} onClose={() => setExplainFeature(null)}>
            <div className="flex gap-2 mb-4">
              <button onClick={() => { setFeatureLang("english"); fetchFeatureExplain(explainFeature.name, explainFeature.value, "english"); }}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold ${featureLang === "english" ? "bg-[#D4A017] text-[#0D1A3A]" : "bg-[#EDF1F8] text-[#3A4557]"}`} data-testid="feature-lang-en">English</button>
              <button onClick={() => { setFeatureLang("hindi"); fetchFeatureExplain(explainFeature.name, explainFeature.value, "hindi"); }}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold ${featureLang === "hindi" ? "bg-[#D4A017] text-[#0D1A3A]" : "bg-[#EDF1F8] text-[#3A4557]"}`} data-testid="feature-lang-hi">Hindi</button>
            </div>
            {featureLoading ? <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 text-[#A87608] animate-spin" /></div>
              : <RichText text={featureExplanation} />}
          </ExplainerSheet>
        )}
      </AnimatePresence>

      {/* Full Policy Explainer */}
      <AnimatePresence>
        {fullExplainOpen && (
          <ExplainerSheet title="Complete Policy Explanation" subtitle={policy.plan_name} onClose={() => setFullExplainOpen(false)}>
            <div className="flex gap-2 mb-4">
              <button onClick={() => { setFullLang("english"); fetchFullExplain("english"); }}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold ${fullLang === "english" ? "bg-[#D4A017] text-[#0D1A3A]" : "bg-[#EDF1F8] text-[#3A4557]"}`}>English</button>
              <button onClick={() => { setFullLang("hindi"); fetchFullExplain("hindi"); }}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold ${fullLang === "hindi" ? "bg-[#D4A017] text-[#0D1A3A]" : "bg-[#EDF1F8] text-[#3A4557]"}`}>Hindi</button>
            </div>
            {fullLoading ? <div className="flex flex-col items-center py-10 gap-2"><Loader2 className="h-8 w-8 text-[#A87608] animate-spin" /><p className="text-[#3A4557] text-xs">{fullLang === "hindi" ? "समझा रहे हैं..." : "Making it simple..."}</p></div>
              : <RichText text={fullExplanation} />}
          </ExplainerSheet>
        )}
      </AnimatePresence>
    </div>
  );
}
